import React, { useState } from 'react';
import {
  ChevronLeft, Send, Trash2, CheckCircle2, XCircle,
  History, Sparkles, FileText, MessageCircle, ArrowRight,
  ShieldCheck, UserCheck, AlertTriangle,
} from 'lucide-react';
import { getStatusMeta, formatDate, formatDateTime, isOverdue, canEditTask } from '../utils/helpers';
import { StatusBadge, PriorityBadge, RiskBadge, InfoItem, FileList, FormTextArea } from './Shared';

export default function TaskDetail({ task, navigateTo, currentUser, userRole, updateTask, deleteTask, addComment, addApproval, changeStatus }) {
  const [activeTab, setActiveTab] = useState('info');
  const [actionText, setActionText] = useState('');
  const [reviewText, setReviewText] = useState('');
  const [commentText, setCommentText] = useState('');
  const [replyText, setReplyText] = useState('');
  const [replyTo, setReplyTo] = useState(null);
  const [approvalComment, setApprovalComment] = useState('');
  const [showApprove, setShowApprove] = useState(false);
  const [showReject, setShowReject] = useState(false);
  const editable = canEditTask(task, currentUser, userRole);

  function handleAddAction() {
    if (!actionText.trim()) return;
    updateTask({
      ...task,
      actions: [...task.actions, { user: currentUser, time: formatDateTime(new Date()), content: actionText }],
      logs: [...task.logs, { time: formatDateTime(new Date()), content: `${currentUser} 添加执行动作：${actionText}`, type: 'status', user: currentUser }],
    });
    setActionText('');
  }

  function handleAddReview() {
    if (!reviewText.trim()) return;
    updateTask({ ...task, reviewResult: reviewText, logs: [...task.logs, { time: formatDateTime(new Date()), content: `添加复盘结果：${reviewText}`, type: 'edit', user: currentUser }] });
    setReviewText('');
  }

  function handleAddComment() {
    if (!commentText.trim()) return;
    addComment(task.id, commentText);
    setCommentText('');
  }

  function handleReply(idx) {
    if (!replyText.trim()) return;
    addComment(task.id, replyText, idx);
    setReplyText('');
    setReplyTo(null);
  }

  function handleApprove() {
    if (!approvalComment.trim()) return;
    addApproval(task.id, 'approve', approvalComment);
    setApprovalComment('');
    setShowApprove(false);
  }

  function handleReject() {
    if (!approvalComment.trim()) return;
    addApproval(task.id, 'reject', approvalComment);
    setApprovalComment('');
    setShowReject(false);
  }

  const tabs = [
    { key: 'info', label: '基础信息' },
    { key: 'materials', label: '输入材料' },
    { key: 'ai', label: 'AI输出结果' },
    { key: 'actions', label: '执行动作' },
    { key: 'approvals', label: '审批意见' },
    { key: 'comments', label: `评论区 (${task.comments?.length || 0})` },
    { key: 'logs', label: '操作日志' },
    { key: 'attachments', label: '附件' },
    { key: 'review', label: '复盘结果' },
  ];

  const statusActions = [];
  if (editable) {
    if (task.status === 'pending') statusActions.push({ label: '开始处理', onClick: () => changeStatus(task.id, 'processing'), color: 'bg-blue-600 hover:bg-blue-700' });
    if (task.status === 'processing') {
      if (task.needLeoApproval) statusActions.push({ label: '提交审核', onClick: () => changeStatus(task.id, 'review'), color: 'bg-amber-600 hover:bg-amber-700' });
      else statusActions.push({ label: '标记完成', onClick: () => changeStatus(task.id, 'completed'), color: 'bg-emerald-600 hover:bg-emerald-700' });
    }
    if (task.status === 'rejected') statusActions.push({ label: '修改后重新提交', onClick: () => changeStatus(task.id, 'review'), color: 'bg-indigo-600 hover:bg-indigo-700' });
    if (task.status === 'overdue' && editable) statusActions.push({ label: '恢复处理', onClick: () => changeStatus(task.id, 'processing'), color: 'bg-blue-600 hover:bg-blue-700' });
  }
  if (userRole?.canArchive && ['completed', 'rejected'].includes(task.status)) {
    statusActions.push({ label: '归档', onClick: () => changeStatus(task.id, 'archived'), color: 'bg-gray-600 hover:bg-gray-700' });
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center">
          <button onClick={() => navigateTo('all')} className="mr-3 text-slate-400 hover:text-slate-600"><ChevronLeft className="w-5 h-5" /></button>
          <div>
            <div className="flex items-center space-x-2">
              <span className="text-xs font-mono text-slate-400">{task.id}</span>
              <StatusBadge status={task.status} />
              <PriorityBadge priority={task.priority} />
              <RiskBadge level={task.riskLevel} />
              {isOverdue(task.dueDate, task.status) && <span className="text-xs font-medium text-rose-600 bg-rose-50 px-2 py-0.5 rounded border border-rose-200">已超时</span>}
            </div>
            <h1 className="text-xl font-bold text-slate-800 mt-1">{task.title}</h1>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          {statusActions.map((a, i) => (
            <button key={i} onClick={a.onClick} className={`px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors ${a.color}`}>{a.label}</button>
          ))}
          {(userRole?.canDeleteAny || (userRole?.canDeleteOwn && task.assignee === currentUser)) && (
            <button onClick={() => { if (confirm('确定删除此任务？')) { deleteTask(task.id); navigateTo('all'); } }} className="p-2 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-colors"><Trash2 className="w-4 h-4" /></button>
          )}
        </div>
      </div>

      <div className="card overflow-hidden">
        <div className="flex items-center border-b border-slate-200 overflow-x-auto">
          {tabs.map(tab => (
            <button key={tab.key} onClick={() => setActiveTab(tab.key)} className={`px-4 py-3 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${activeTab === tab.key ? 'border-indigo-600 text-indigo-700' : 'border-transparent text-slate-500 hover:text-slate-700'}`}>
              {tab.label}
            </button>
          ))}
        </div>
        <div className="p-6">
          {activeTab === 'info' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <InfoItem label="任务标题" value={task.title} />
              <InfoItem label="任务类型" value={task.type} />
              <InfoItem label="ASIN" value={task.asin || '-'} />
              <InfoItem label="SKU" value={task.sku || '-'} />
              <InfoItem label="店铺" value={task.store} />
              <InfoItem label="站点" value={task.site} />
              <InfoItem label="负责人" value={task.assignee} />
              <InfoItem label="协作人" value={task.collaborators.join(', ') || '-'} />
              <InfoItem label="优先级" value={<PriorityBadge priority={task.priority} />} />
              <InfoItem label="风险等级" value={<RiskBadge level={task.riskLevel} />} />
              <InfoItem label="预计完成时间" value={formatDate(task.dueDate)} />
              <InfoItem label="创建时间" value={formatDateTime(new Date(task.createdAt))} />
              <InfoItem label="需Leo审批" value={task.needLeoApproval ? '是' : '否'} />
              <InfoItem label="标签" value={task.tags?.length ? task.tags.map(t => <span key={t} className="inline-block mr-1 px-2 py-0.5 rounded text-xs bg-slate-100 text-slate-600">{t}</span>) : '-'} />
              <div className="md:col-span-2"><InfoItem label="任务描述" value={task.description || '-'} /></div>
            </div>
          )}

          {activeTab === 'materials' && <div className="text-sm text-slate-700 whitespace-pre-wrap">{task.materials || '暂无输入材料'}</div>}

          {activeTab === 'ai' && (
            task.aiOutput ? (
              <div className="p-4 bg-indigo-50 border border-indigo-100 rounded-lg text-sm text-indigo-900 whitespace-pre-wrap"><Sparkles className="w-5 h-5 inline mr-2 text-indigo-500" />{task.aiOutput}</div>
            ) : <div className="text-sm text-slate-400">暂无AI输出结果</div>
          )}

          {activeTab === 'actions' && (
            <div className="space-y-4">
              <div className="space-y-3">
                {task.actions.length === 0 && <div className="text-sm text-slate-400">暂无执行动作</div>}
                {task.actions.map((a, i) => (
                  <div key={i} className="flex items-start space-x-3 p-3 bg-slate-50 rounded-lg">
                    <div className="w-7 h-7 rounded-full bg-indigo-100 text-indigo-700 flex items-center justify-center text-xs font-semibold shrink-0">{a.user.slice(0, 1)}</div>
                    <div>
                      <div className="text-sm font-medium text-slate-800">{a.user} <span className="text-xs font-normal text-slate-400 ml-2">{a.time}</span></div>
                      <div className="text-sm text-slate-600 mt-0.5">{a.content}</div>
                    </div>
                  </div>
                ))}
              </div>
              {editable && (
                <div className="flex items-start space-x-2 pt-4 border-t border-slate-100">
                  <textarea className="flex-1 text-sm border border-slate-200 rounded-lg p-2 focus:outline-none focus:ring-2 focus:ring-indigo-500" rows={2} placeholder="记录执行动作..." value={actionText} onChange={e => setActionText(e.target.value)} />
                  <button onClick={handleAddAction} className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg"><Send className="w-4 h-4" /></button>
                </div>
              )}
            </div>
          )}

          {activeTab === 'approvals' && (
            <div className="space-y-4">
              {task.approvals.length === 0 && <div className="text-sm text-slate-400">暂无审批记录</div>}
              {task.approvals.map((a, i) => (
                <div key={i} className={`p-4 rounded-lg border ${a.decision === 'approve' ? 'bg-emerald-50 border-emerald-200' : 'bg-rose-50 border-rose-200'}`}>
                  <div className="flex items-center space-x-2">
                    {a.decision === 'approve' ? <CheckCircle2 className="w-5 h-5 text-emerald-600" /> : <XCircle className="w-5 h-5 text-rose-600" />}
                    <span className="text-sm font-semibold text-slate-800">{a.user}</span>
                    <span className="text-xs text-slate-400">{a.time}</span>
                    <span className="text-xs font-medium px-2 py-0.5 rounded bg-slate-200 text-slate-700">第{a.round}轮</span>
                    <span className={`text-xs font-medium px-2 py-0.5 rounded ${a.decision === 'approve' ? 'bg-emerald-200 text-emerald-800' : 'bg-rose-200 text-rose-800'}`}>{a.decision === 'approve' ? '通过' : '驳回'}</span>
                  </div>
                  <div className="text-sm text-slate-700 mt-2">{a.comment}</div>
                </div>
              ))}

              {userRole?.canApprove && task.status === 'review' && (
                <div className="pt-4 border-t border-slate-100">
                  <div className="flex items-center space-x-2 mb-3">
                    <button onClick={() => { setShowApprove(true); setShowReject(false); }} className="px-3 py-1.5 text-sm font-medium text-white bg-emerald-600 hover:bg-emerald-700 rounded-lg">
                      <CheckCircle2 className="w-4 h-4 inline mr-1" /> 审批通过
                    </button>
                    <button onClick={() => { setShowReject(true); setShowApprove(false); }} className="px-3 py-1.5 text-sm font-medium text-white bg-rose-600 hover:bg-rose-700 rounded-lg">
                      <XCircle className="w-4 h-4 inline mr-1" /> 驳回
                    </button>
                  </div>
                  {(showApprove || showReject) && (
                    <div className="flex items-start space-x-2">
                      <textarea className="flex-1 text-sm border border-slate-200 rounded-lg p-2 focus:outline-none focus:ring-2 focus:ring-indigo-500" rows={2} placeholder="请输入审批意见..." value={approvalComment} onChange={e => setApprovalComment(e.target.value)} />
                      <button onClick={showApprove ? handleApprove : handleReject} className={`px-4 py-2 text-sm font-medium text-white rounded-lg ${showApprove ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-rose-600 hover:bg-rose-700'}`}>确认</button>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {activeTab === 'comments' && (
            <div className="space-y-4">
              {(!task.comments || task.comments.length === 0) && <div className="text-sm text-slate-400">暂无评论</div>}
              {task.comments?.map((c, idx) => (
                <div key={idx} className="p-3 bg-slate-50 rounded-lg">
                  <div className="flex items-center space-x-2">
                    <div className="w-6 h-6 rounded-full bg-indigo-100 text-indigo-700 flex items-center justify-center text-xs font-semibold">{c.user.slice(0, 1)}</div>
                    <span className="text-sm font-medium text-slate-800">{c.user}</span>
                    <span className="text-xs text-slate-400">{c.time}</span>
                  </div>
                  <div className="text-sm text-slate-700 mt-1">{c.content}</div>
                  {c.replies && c.replies.length > 0 && (
                    <div className="mt-2 ml-6 space-y-2">
                      {c.replies.map((r, ri) => (
                        <div key={ri} className="p-2 bg-white rounded border border-slate-100">
                          <div className="flex items-center space-x-2">
                            <span className="text-xs font-medium text-slate-700">{r.user}</span>
                            <span className="text-xs text-slate-400">{r.time}</span>
                          </div>
                          <div className="text-sm text-slate-600 mt-0.5">{r.content}</div>
                        </div>
                      ))}
                    </div>
                  )}
                  <button onClick={() => setReplyTo(replyTo === idx ? null : idx)} className="text-xs text-indigo-600 hover:text-indigo-800 mt-2">{replyTo === idx ? '取消回复' : '回复'}</button>
                  {replyTo === idx && (
                    <div className="flex items-start space-x-2 mt-2">
                      <textarea className="flex-1 text-sm border border-slate-200 rounded-lg p-2 focus:outline-none focus:ring-2 focus:ring-indigo-500" rows={2} placeholder="回复..." value={replyText} onChange={e => setReplyText(e.target.value)} />
                      <button onClick={() => handleReply(idx)} className="px-3 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg">发送</button>
                    </div>
                  )}
                </div>
              ))}
              <div className="flex items-start space-x-2 pt-4 border-t border-slate-100">
                <textarea className="flex-1 text-sm border border-slate-200 rounded-lg p-2 focus:outline-none focus:ring-2 focus:ring-indigo-500" rows={2} placeholder="添加评论..." value={commentText} onChange={e => setCommentText(e.target.value)} />
                <button onClick={handleAddComment} className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg">发送</button>
              </div>
            </div>
          )}

          {activeTab === 'logs' && (
            <div className="space-y-2">
              {task.logs.map((l, i) => (
                <div key={i} className="flex items-start space-x-3 text-sm">
                  <History className="w-4 h-4 text-slate-400 shrink-0 mt-0.5" />
                  <span className="text-slate-400 w-36 shrink-0">{l.time}</span>
                  <span className="text-slate-500 w-16 shrink-0">[{l.user || '系统'}]</span>
                  <span className="text-slate-700">{l.content}</span>
                </div>
              ))}
            </div>
          )}

          {activeTab === 'attachments' && <FileList files={task.files} />}

          {activeTab === 'review' && (
            <div className="space-y-4">
              {task.reviewResult ? <div className="p-4 bg-slate-50 border border-slate-200 rounded-lg text-sm text-slate-700 whitespace-pre-wrap">{task.reviewResult}</div> : <div className="text-sm text-slate-400">暂无复盘结果</div>}
              {editable && (
                <div className="flex items-start space-x-2 pt-4 border-t border-slate-100">
                  <textarea className="flex-1 text-sm border border-slate-200 rounded-lg p-2 focus:outline-none focus:ring-2 focus:ring-indigo-500" rows={3} placeholder="添加复盘总结..." value={reviewText} onChange={e => setReviewText(e.target.value)} />
                  <button onClick={handleAddReview} className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg">保存</button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
